import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { getAccessToken, hasMagicLinkSession } from "../helpers/TokenRefresh";
import { Person } from "../types/person.type";

type UserContextType = {
  userRights: string[];
  userRightsLoading: boolean;
  currentPerson: Person | null;
  myChildren: Person[];
  refreshUserRights: () => Promise<void>;
};

const UserContext = createContext<UserContextType>({
  userRights: [],
  userRightsLoading: true,
  currentPerson: null,
  myChildren: [],
  refreshUserRights: async () => {},
});

export const useUserContext = () => useContext(UserContext);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userRights, setUserRights] = useState<string[]>([]);
  const [userRightsLoading, setUserRightsLoading] = useState<boolean>(true);
  const [currentPerson, setCurrentPerson] = useState<Person | null>(null);
  const [myChildren, setMyChildren] = useState<Person[]>([]);
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance, accounts } = useMsal();

  const fetchRightsAndPerson = async (token: string) => {
    const headers = { Authorization: `Bearer ${token}` };
    const base = import.meta.env.VITE_X_API_URL || '';
    try {
      const [rightsRes, personRes, childrenRes] = await Promise.all([
        fetch(`${base}/person/me/rights`, { headers }),
        fetch(`${base}/person/me`, { headers }),
        fetch(`${base}/person/me/children`, { headers }),
      ]);
      setUserRights(rightsRes.ok ? ((await rightsRes.json()).rights ?? []) : []);
      setCurrentPerson(personRes.ok ? await personRes.json() : null);
      setMyChildren(childrenRes.ok ? await childrenRes.json() : []);
    } catch {
      setUserRights([]);
      setCurrentPerson(null);
      setMyChildren([]);
    }
  };

  const ensureTokenAndFetch = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        setUserRights([]);
        setCurrentPerson(null);
        setMyChildren([]);
        return;
      }
      await fetchRightsAndPerson(token);
    } finally {
      setUserRightsLoading(false);
    }
  };

  const activeAccountId = accounts[0]?.homeAccountId ?? null;

  useEffect(() => {
    if (inProgress !== 'none') return;

    const hasAccounts = accounts.length > 0 || instance.getAllAccounts().length > 0 || hasMagicLinkSession();
    if (!hasAccounts) {
      setUserRights([]);
      setCurrentPerson(null);
      setMyChildren([]);
      setUserRightsLoading(false);
      return;
    }

    setUserRightsLoading(true);
    ensureTokenAndFetch();
  }, [inProgress, isAuthenticated, activeAccountId]);

  const refreshUserRights = async () => {
    await ensureTokenAndFetch();
  };

  return (
    <UserContext.Provider value={{ userRights, userRightsLoading, currentPerson, myChildren, refreshUserRights }}>
      {children}
    </UserContext.Provider>
  );
};
